// Angular
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
// RxJS
import { tap, withLatestFrom, filter, mergeMap } from 'rxjs/operators';
import { of, Observable, defer } from 'rxjs';
// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
// Auth actions
import { AuthActionTypes, Login, Logout, Register, UserRequested, UserLoaded } from '../_actions/auth.actions';
import { AuthService } from '../_services/index';
import { AppState } from '../../reducers';
import { environment } from '../../../../environments/environment';
import { isUserLoaded } from '../_selectors/auth.selectors';
import { UserService } from '../_services/user.service';
import { SettingsService } from '../../../views/pages/configuration/settings/settings.service';
import { PageSettingsData } from '../../configuration/_models/config-settings.model';
import { FileBrowserService } from '../../file-browser/file-browser.service';
import {
    getFirstAvailableModuleUrl,
    getRouteSegmentFromUrl,
    hasModuleRouteAccess,
    isModuleRoutePath,
    normalizeModuleIds
} from '../_utils/module-navigation.util';
@Injectable()
export class AuthEffects {
    private readonly tenantModuleAccessStorageKey = 'tenantModuleAccess';
    userProjection: string = [
        "source", "name", "email", "phone", "username", "admin", "plantAdmin",
        "optWhatsapp", "moduleAccess", "facility", "unit", "system", "enterprise",
        "facilityAdminList", "createdTime", "modifiedTime", "notifications", "notificationSettings",
        "internal", "genAiEnabled", "rcaAlertList", "activeHours", "modelRetraining", "lastActiveAt",
        "loggedInAt", "loggedOutAt", "idptype",
        "alertTableColumns", "caseTableColumns", "graphViewPreference", "docPreferences", "active", "_id", "audibleAlarmSettings"
    ].join(",");

    constructor(private actions$: Actions,
        private readonly settingsService: SettingsService,
        private readonly router: Router,
        private readonly auth: AuthService,
        private readonly store: Store<AppState>,
        private readonly fileBrowserService: FileBrowserService,
        private readonly userService:UserService) { }

    login$ = createEffect(() => this.actions$.pipe(
        ofType<Login>(AuthActionTypes.Login),
        tap(action => {
            localStorage.setItem(environment.runtime.authTokenKey, action.payload.authToken);
            localStorage.setItem(environment.runtime.refreshTokenKey, action.payload.refreshToken)
            this.store.dispatch(new UserRequested());
        }),
    ), {dispatch: false});

    logout$ = createEffect(() => this.actions$.pipe(
        ofType<Logout>(AuthActionTypes.Logout),
        tap(() => {
            const userEmail = this.userService.getLoggedInUser()?.email;
            localStorage.removeItem(environment.runtime.authTokenKey);
            localStorage.removeItem(environment.runtime.refreshTokenKey);
            localStorage.removeItem('user');
            localStorage.removeItem('alertFilters');
            localStorage.removeItem('tabColumnMap');
            localStorage.removeItem('azureSas');
		    localStorage.removeItem('awsAccessToken');
            this.fileBrowserService.clearFileBrowserData();
            if (environment.runtime.enableSAMLSSO && environment.runtime.DIRECT_REDIRECT_SAML && environment.runtime.SAML_LOGOUT_REDIRECT_URL) {
                this.auth.redirectToSAMLLogout(userEmail);
            } else {
                this.router.navigate(['/web/login'], { queryParams: { returnUrl: this.router.url } });
            }
        })
    ), {dispatch: false});


    register$ = createEffect(() => this.actions$.pipe(
        ofType<Register>(AuthActionTypes.Register),
        tap(action => {
            localStorage.setItem(environment.runtime.authTokenKey, action.payload.authToken);
            this.store.dispatch(new UserRequested());
        })
    ), {dispatch: false});


    loadUser$ = createEffect(() => this.actions$
    .pipe(
        ofType<UserRequested>(AuthActionTypes.UserRequested),
        withLatestFrom(this.store.pipe(select(isUserLoaded))),
        filter(([action, _isUserLoaded]) => !_isUserLoaded),
        mergeMap(([action, _isUserLoaded]) => this.auth.getUserByToken(this.userProjection)),
        tap(_user => {
            if (_user) {
                this.userService.user = _user
                localStorage.setItem('user', JSON.stringify(_user));
                if (window !== undefined) {
                    const url = getRouteSegmentFromUrl(window.location?.pathname || '');
                    const returnUrl = this._getReturnUrlFromLocation();
                    const returnUrlPage = getRouteSegmentFromUrl(returnUrl);

                    this.settingsService.getAllPageSettings();
                    // Merge incoming authenticated configuration with the existing runtime.
                    // This is critical to preserve pre-login fields (like authTokenKey).
                    // For newly created tenant, when enterpriseId does not exist, full
                    // replace would wipe the runtime, trapping them in a login loop.
                    this.settingsService.getPageSettings('environmentConfiguration').subscribe(() => {
                        environment.runtime = { ...environment.runtime, ...this.settingsService.extractEnvironmentConfig() };
                    });
                    this.settingsService.getPageSettings('tenantModuleAccess').subscribe((data: PageSettingsData) => {
                        const tenantModuleAccess = normalizeModuleIds(
                            data?.settings?.find(s => s.fieldKey === 'tenantModuleAccess')?.value || []
                        );
                        localStorage.setItem(this.tenantModuleAccessStorageKey, JSON.stringify(tenantModuleAccess));

                        const fallbackUrl = getFirstAvailableModuleUrl(_user?.moduleAccess || [], tenantModuleAccess);
                        const isAuthPage = ['login', 'forgot-password', 'verify-otp'].includes(url);
                        const isOnModuleRoute = isModuleRoutePath(url);

                        // 1) Login returnUrl support: normal route -> allow, module route -> allow only if accessible.
                        const isUserAdmin = Boolean((_user as any)?.admin || (_user as any)?.isAdmin || (_user as any)?.plantAdmin || (_user as any)?.isPlantAdmin);
                        if (returnUrl && isAuthPage) {
                            const canUseReturnUrl = !isModuleRoutePath(returnUrlPage)
                                || isUserAdmin
                                || hasModuleRouteAccess(returnUrlPage, _user?.moduleAccess || [], tenantModuleAccess);
                            this.router.navigateByUrl(canUseReturnUrl ? returnUrl : fallbackUrl);
                            return;
                        }

                        // 2) Direct module navigation for non-admin users.
                        const isTriageSuite = ['alert-triage', 'asset-explorer', 'knowledge-graph', 'library'].includes(url);
                        if (isOnModuleRoute && !isUserAdmin && !isTriageSuite) {
                            const hasCurrentModuleAccess = hasModuleRouteAccess(
                                url,
                                _user?.moduleAccess || [],
                                tenantModuleAccess
                            );
                            if (!hasCurrentModuleAccess) {
                                this.router.navigateByUrl(fallbackUrl);
                                return;
                            }
                        }

                        if (!url || isAuthPage) {
                            this.router.navigateByUrl(fallbackUrl);
                        }
                    });
                }

                // localStorage.setItem('user', JSON.stringify(_user));
                this.store.dispatch(new UserLoaded({ user: _user }));
            } else {
                this.store.dispatch(new Logout());
            }
        })
      ), {dispatch: false});


    init$: Observable<Action> = createEffect(() => defer(() => {
        const userToken = localStorage.getItem(environment.runtime.authTokenKey);
        const refreshToken = localStorage.getItem(environment.runtime.refreshTokenKey);
        let observableResult = of({type: 'NO_ACTION'});
        if (userToken) {
            //observableResult = of(new Login({  authToken: userToken, type: type }));
            observableResult = of(new Login({ authToken: userToken, refreshToken: refreshToken }));
        }
        return observableResult;
    }));

    private _getReturnUrlFromLocation = (): string => {
        try {
            const href = window.location?.href || '';
            const parsedUrl = new URL(href);
            const urlParam = parsedUrl.searchParams.get('returnUrl');
            if (urlParam) return urlParam;
            const samlReturnUrl = sessionStorage.getItem('samlReturnUrl');
            if (samlReturnUrl) {
                sessionStorage.removeItem('samlReturnUrl');
                return samlReturnUrl;
            }
            return '';
        } catch (error) {
            return '';
        }
    }
}
